import { useMemo, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SheetMirrorRowDto, SheetMirrorTabKey } from '@bhops/shared';
import { sheetMirrorApi } from '@/api/sheetMirror';
import { extractApiError } from '@/api/client';
import { Button, Input, Select } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { useAuthStore } from '@/stores/auth';
import { canEditAny } from '@/permissions';
import { SheetGrid } from './SheetGrid';
import { SheetNewRowBar } from './SheetNewRowBar';
import { SheetTopSummary } from './SheetTopSummary';
import { parseSheetTop, shareColors } from './sheetTop';
import { currentMonthKey, dateLike, weekAndDay } from './sheetDates';
import { ContactMark, MethodMark } from './sheetMarks';
import {
  sortSheetRows,
  type SheetChipColors,
  type SheetSort,
  type SheetWidths,
} from './sheetTable';
import { DRAFT, agoText, describeEdit, useSheetMirrorEditor } from './useSheetMirrorEditor';

// BUSINESS '도입 문의 현황'(2026-10-23) — 오너: "도입 문의 현황은 안만들었네?" → 고칠 수 있게, 26.01 부터.
// 구글 시트 '인바운드 문의 현황' 의 달 탭 '도입문의 현황 (YYYY_M) 수기' 를 그대로 보여 주고 고친다.
//   · 칸 · 순서 · 드롭다운은 시트 그대로. 머리글이 비었는데 값이 있는 열만 열 글자('AE열')로 부른다(오너 선택).
//   · 탭 위쪽 요약(성과 · 목표 · 주차별 IB · 통화 결과 · 권역/담당자 비율)은 시트에 보이는 값 그대로 카드로
//     (오너 "그 위에 있는 것들도 콘솔에 맞게") — 읽기만, 담당자 · 주차 칸은 눌러서 아래 표를 거른다.
//   · 칸에서 바로 고친다(SheetGrid) — 1분 안에 시트에 쓰이고, 그사이 시트에서 같은 칸이 바뀌었으면 쓰지 않는다.

const GROUP = 'inquiry' as const;
/** 왼쪽 정렬 · 펼쳐 쓰는 긴 글 칸 */
const LONG_KEYS = new Set(['상담 내용', '기타사항', 'MKT comment']);
const NAME = '기업/상호';
const METHOD = '문의 방법';
const ROUTE = '유입경로';
const OWNER = '담당자';
const WEEK = '주차';
const DAY = '요일';
/** 이 날짜 칸을 고르면 같은 줄의 주차 · 요일도 채운다(시트에서 손으로 넣는 값, 수식 아님 — 사본 시트 확인) */
const DATE_KEY = '문의 일자';
/**
 * 시트에 드롭다운이 없는 글자 칸 — 콘솔에서는 이 탭에 이미 쓰인 값으로 드롭다운을 만든다(오너 "유입경로도
 * 드롭다운으로 고를 수 있게"). 시트에 드롭다운이 생기면 시트 목록을 그대로 쓴다.
 */
const SUGGEST_KEYS = new Set(['유입경로']);
/**
 * 시트에 날짜를 **글자**로 쓰는 칸('10.12' — 사본 시트 AC21 확인) — 콘솔은 달력으로 고르고, 시트에는 그 열에 이미
 * 쓰인 모양의 글자로 쓴다(시트 형식 그대로). 오너 "시연 예정이랑 시연 완료도 문의 일자랑 같은 조건".
 * 시트에서 날짜 값인 달이면 스크립트가 이미 날짜 칸으로 보내 그대로 날짜로 쓴다.
 */
const TEXT_DATE_KEYS = new Set(['시연 예정', '시연 완료']);
/** 글자 날짜 칸에 아직 아무 값도 없을 때의 모양(사본 시트 '10.12') */
const TEXT_DATE_SAMPLE = '10.12';

/**
 * 칸 기본 폭(px) — 2026-10-23 오너가 화면에서 맞춘 폭("표 간격은 지금 내가 맞춰둔걸 기본으로").
 * 여기 없는 칸(시트에 새로 생긴 칸)은 시트 열 너비. 머리글 끝을 두 번 누르면 이 폭으로 돌아간다.
 */
const DEFAULT_WIDTHS: SheetWidths = {
  '문의 일자': 62,
  주차: 40,
  요일: 40,
  '기업/상호': 149,
  '문의 방법': 79,
  유입경로: 79,
  메뉴: 79,
  지역1: 68,
  지역2: 68,
  전화번호: 109,
  기타사항: 79,
  '카카오톡 서베이 제출': 79,
  담당자: 79,
  '통화 날짜': 79,
  '통화 시간': 79,
  '통화 여부': 40,
  '콜백 여부': 40,
  리탭: 40,
  '매장 운영': 40,
  '신규 창업': 40,
  '초벌 여부': 40,
  '테이블 그릴링': 51,
  '테이 블수': 40,
  '상담 내용': 269,
  도입목적: 79,
  '가볼만한 도입 매장 안내': 79,
  '시연 희망': 40,
  '시연 예정': 75,
  '시연 완료': 79,
  Topic: 79,
  'MKT comment': 169,
};

/**
 * 드롭다운 칩 색 — 사본 시트 '도입문의 현황 (2026_10) 수기' 의 드롭다운 설정과 같게(2026-10-23 오너 화면).
 * 구글이 칩 색을 스크립트에 주지 않아 여기 적어 둔다. 시트에서 색을 바꾸거나 값이 늘면 여기도 고친다
 * (없는 값 · 다른 드롭다운은 회색 칩). 지역은 권역별로 같은 색. 담당자 칩은 여기 없다 — 시트 위쪽 담당자
 * 비율 칸의 색을 그때그때 쓴다(shareColors).
 */
const REGION = (names: string[], c: readonly [string, string]) =>
  Object.fromEntries(names.map((n) => [n, c] as const));
const REGION_COLORS: SheetChipColors = {
  지역1: {
    ...REGION(['서울', '경기', '인천'], ['#ffcfc9', '#b10202']),
    ...REGION(['대구', '경남', '부산', '경북', '울산'], ['#bfe1f6', '#0a53a8']),
    ...REGION(['전남', '전북', '광주'], ['#ffe5a0', '#473821']),
    ...REGION(['충남', '세종', '충북', '대전'], ['#d4edbc', '#11734b']),
    ...REGION(['제주', '강원'], ['#e6cff2', '#5a3286']),
    해외: ['#3d3d3d', '#ffffff'],
  },
};

export function InquiryPage() {
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = !!role && canEditAny(role);

  const monthsQ = useQuery({
    queryKey: ['sheet-mirror', 'groups', GROUP],
    queryFn: () => sheetMirrorApi.months(GROUP),
    refetchInterval: 60_000,
    retry: false,
  });
  const months = monthsQ.data ?? [];
  const [picked, setPicked] = useState<SheetMirrorTabKey | null>(null);
  const key = picked ?? currentMonthKey(months);

  const { q, tab, draft, setDraft, problems, commit, addMx, buildRows, withPending, undo, redo } =
    useSheetMirrorEditor(key);

  const [search, setSearch] = useState('');
  const [method, setMethod] = useState('');
  const [route, setRoute] = useState('');
  const [owner, setOwner] = useState('');
  /** 요약의 'N주차' 칸으로 고른 주차('1') */
  const [week, setWeek] = useState('');
  const [hideEmpty, setHideEmpty] = useState(false);
  const [sort, setSort] = useState<SheetSort>(null);

  const allRows = useMemo(() => tab?.rows ?? [], [tab]);
  const has = (k: string) => (tab?.columns ?? []).some((c) => c.key === k);

  /** 표 칸 — 시트 순서 그대로, 숨긴 칸은 빼고(빈 칸 숨기기를 켜면 모두 빈 칸도) */
  const gridCols = useMemo(
    () =>
      (tab?.columns ?? [])
        .filter(
          (c) =>
            !c.hidden &&
            !isRowNumberCol(c.key, allRows) &&
            (!hideEmpty || allRows.some((r) => (r.cells[c.key] ?? '').trim())),
        )
        .map((c) =>
          SUGGEST_KEYS.has(c.key) && c.type === 'text'
            ? { ...c, type: 'list' as const, options: countBy(allRows, c.key).map(([v]) => v) }
            : TEXT_DATE_KEYS.has(c.key) && c.type === 'text'
              ? { ...c, type: 'date' as const }
              : c,
        ),
    [tab?.columns, allRows, hideEmpty],
  );
  /** 문의 일자를 고르면 주차 · 요일도(그 칸이 탭에 있을 때만) */
  const commitCell: typeof commit = (row, c, value, also) => {
    const wd = c.key === DATE_KEY ? weekAndDay(value) : null;
    // 글자 날짜 칸은 그 열에 보이는 모양의 글자로('2026-10-21' → '10.21')
    if (TEXT_DATE_KEYS.has(c.key) && tab?.columns.find((x) => x.key === c.key)?.type === 'text') {
      const sample = allRows.find((r) => (r.cells[c.key] ?? '').trim())?.cells[c.key];
      value = dateLike(value, sample ?? TEXT_DATE_SAMPLE);
    }
    return commit(row, c, value, {
      ...also,
      ...(wd && has(WEEK) ? { [WEEK]: wd.week } : null),
      ...(wd && has(DAY) ? { [DAY]: wd.day } : null),
    });
  };

  const methods = useMemo(() => countBy(allRows, METHOD), [allRows]);
  const routes = useMemo(() => countBy(allRows, ROUTE), [allRows]);
  const owners = useMemo(() => countBy(allRows, OWNER), [allRows]);
  const summary = useMemo(() => parseSheetTop(tab?.top), [tab?.top]);
  // 담당자 칩 색은 시트 위쪽 담당자 비율 칸의 색(담당자가 늘거나 빠져도 시트를 따라간다)
  const chipColors = useMemo<SheetChipColors>(
    () => ({ ...REGION_COLORS, [OWNER]: shareColors(summary) }),
    [summary],
  );

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    const list = allRows.filter((r) => {
      if (method && (r.cells[METHOD] ?? '') !== method) return false;
      if (route && (r.cells[ROUTE] ?? '') !== route) return false;
      if (owner && (r.cells[OWNER] ?? '') !== owner) return false;
      if (week && (r.cells[WEEK] ?? '').trim() !== week) return false;
      if (s && !Object.values(r.cells).some((v) => v.toLowerCase().includes(s))) return false;
      return true;
    });
    // 기본은 최근 문의 먼저(시트 아래쪽부터) — 머리글을 눌러 다른 순서로
    return sortSheetRows(withPending([...list].reverse()), sort);
  }, [allRows, search, method, route, owner, week, sort, withPending]);

  const gridRows = useMemo(() => buildRows(rows, gridCols), [buildRows, rows, gridCols]);

  const pickMonth = (k: SheetMirrorTabKey) => {
    setPicked(k);
    setMethod('');
    setRoute('');
    setOwner('');
    setWeek('');
  };
  /** 요약 칸 누르기 — 'N주차' 는 주차, 그 밖은 담당자 */
  const pickTop = (label: string) => {
    const w = /^(\d+)\s*주차$/.exec(label);
    if (!label) {
      setOwner('');
      setWeek('');
    } else if (w) setWeek(week === w[1] ? '' : w[1]!);
    else setOwner(owner === label ? '' : label);
  };

  if (monthsQ.isLoading) return <div style={page}>불러오는 중…</div>;
  if (monthsQ.isError || !months.length)
    return (
      <div style={page}>
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          도입 문의 현황(수기)
        </h1>
        <div style={notice} data-testid="inquiry-not-connected">
          {monthsQ.isError
            ? extractApiError(monthsQ.error)
            : '아직 시트와 연결되지 않았습니다. 시트의 앱스 스크립트가 한 번 실행되면 보입니다.'}
        </div>
      </div>
    );

  const count = (
    <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
      {rows.length}건
    </span>
  );
  // 표 왼쪽 위 [+ 새 줄] · 건수(2026-10-23 오너 "이건 + 새 줄 버튼 오른쪽에")
  const newRowBar = canEdit ? (
    <SheetNewRowBar
      drafting={!!draft}
      canAdd={!!(draft?.[NAME] ?? '').trim()}
      adding={addMx.isPending}
      count={count}
      hint={
        <>
          표 맨 위 줄의 칸을 채우고 [시트에 추가]를 눌러 주세요. &lsquo;{NAME}&rsquo;는 꼭 넣어야
          합니다. {tab?.sheetName} 탭의 마지막 문의 다음 줄에 쓰입니다.
        </>
      }
      onStart={() => setDraft((d) => d ?? {})}
      onCancel={() => setDraft(null)}
      onAdd={() => draft && addMx.mutate(draft)}
    />
  ) : (
    count
  );

  return (
    <div style={page}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ marginRight: 'auto' }}>
          <h1 className="kuun-h2" style={{ margin: 0 }}>
            도입 문의 현황(수기)
          </h1>
          <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 4 }}>
            {tab ? (
              <>
                구글 시트 &lsquo;{tab.sheetName}&rsquo; 탭과 1분마다 주고받습니다.{' '}
                {tab.lastSyncAt
                  ? `마지막으로 받은 때는 ${agoText(tab.lastSyncAt)}입니다.`
                  : '아직 받은 적이 없습니다.'}
              </>
            ) : (
              '구글 시트 도입문의 현황 수기 탭'
            )}
          </div>
        </div>
        {tab && (
          <a href={tab.sheetUrl} target="_blank" rel="noreferrer" style={linkBtn}>
            시트 열기
          </a>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} role="tablist" aria-label="달">
        {months.map((m) => (
          <button
            key={m.key}
            type="button"
            role="tab"
            aria-selected={m.key === key}
            onClick={() => pickMonth(m.key)}
            className="kuun-sheet-kpi"
            style={{ ...chip, ...(m.key === key ? chipOn : null) }}
            data-testid={`inquiry-month-${m.key}`}
          >
            {monthLabel(m.month)} <span style={{ opacity: 0.7 }}>{m.rowCount}</span>
          </button>
        ))}
      </div>

      {q.isError && <div style={notice}>{extractApiError(q.error)}</div>}
      {tab?.lastError && <div style={notice}>{tab.lastError}</div>}
      {tab && problems.length > 0 && (
        <div style={notice} data-testid="sheet-edit-problems">
          {problems.slice(0, 5).map((e) => (
            <div key={e.id}>
              {describeEdit(e, tab)} 칸을 시트에 쓰지 못했습니다. {e.message}
            </div>
          ))}
        </div>
      )}

      {tab && (
        <>
          <SheetTopSummary
            summary={summary}
            pick={{
              pickable: (l) =>
                (has(WEEK) && /^\d+\s*주차$/.test(l)) || owners.some(([o]) => o === l),
              active: owner || (week ? `${week}주차` : ''),
              onPick: pickTop,
            }}
            titleOf={topTitle}
            markOf={(l, color) => ContactMark({ label: l, color })}
            lead={{
              title: '문의 방법',
              items: [
                {
                  label: '전체 문의',
                  value: String(allRows.length),
                  active: !method,
                  onClick: () => setMethod(''),
                  testId: 'inquiry-total',
                },
                ...methods.map(([m, n]) => ({
                  label: m,
                  value: String(n),
                  mark: <MethodMark method={m} />,
                  active: method === m,
                  onClick: () => setMethod(method === m ? '' : m),
                })),
              ],
            }}
          />

          {/* 새 줄 작성 중에는 [시트에 추가] · [취소] 가 검색 칸 자리로 올라오고, 끝나면 돌아간다(2026-10-23 오너) */}
          {draft && newRowBar ? (
            newRowBar
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Input
                placeholder="상호, 전화번호, 상담 내용 검색"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 240 }}
                aria-label="검색"
              />
              {has(ROUTE) && routes.length > 0 && (
                <Select
                  value={route}
                  onChange={(e) => setRoute(e.target.value)}
                  aria-label="유입경로"
                  style={{ width: 200 }}
                >
                  <option value="">유입경로 전체</option>
                  {routes.map(([r, n]) => (
                    <option key={r} value={r}>
                      {r} ({n})
                    </option>
                  ))}
                </Select>
              )}
              {week && (
                <Button variant="secondary" type="button" onClick={() => setWeek('')}>
                  {week}주차 <Icon name="x" size={12} aria-hidden />
                </Button>
              )}
              {has(OWNER) && owners.length > 0 && (
                <Select
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                  aria-label="담당자"
                  style={{ width: 140 }}
                >
                  <option value="">담당자 전체</option>
                  {owners.map(([o, n]) => (
                    <option key={o} value={o}>
                      {o} ({n})
                    </option>
                  ))}
                </Select>
              )}
              <label className="kuun-caption" style={checkLabel}>
                <input
                  type="checkbox"
                  checked={hideEmpty}
                  onChange={(e) => setHideEmpty(e.target.checked)}
                />
                빈 칸 숨기기
              </label>
            </div>
          )}

          <div style={tableBlock}>
            {!draft && newRowBar}
            <SheetGrid
              columns={gridCols}
              rows={gridRows}
              longKeys={LONG_KEYS}
              colors={chipColors}
              widths={DEFAULT_WIDTHS}
              readOnly={!canEdit}
              onCommit={commitCell}
              focusRow={draft ? DRAFT : null}
              sort={sort}
              onSort={setSort}
              onUndo={() => void undo()}
              onRedo={() => void redo()}
            />
          </div>
        </>
      )}
    </div>
  );
}

/**
 * 시트에 제목 칸이 없는 요약 묶음의 제목 — 통화 · 부재 · 재통화 → '연락 여부'(2026-10-23 오너),
 * '서울+경기권 · 나머지권역' → '권역', 'Jake · Dylan' 같은 담당자 비율 → '담당자'.
 */
function topTitle(labels: string[]): string | undefined {
  if (labels.some((l) => /^(통화|부재|재통화|콜백)$/.test(l))) return '연락 여부';
  if (labels.some((l) => /권/.test(l))) return '권역';
  if (labels.length && labels.every((l) => /^[A-Za-z][A-Za-z .]*$/.test(l))) return '담당자';
  return undefined;
}

/**
 * 머리글 없는 번호 열('A열' — 26.09 이전 탭 맨 앞의 200 · 199 · 198 …)은 콘솔에서 뺀다(2026-10-23 오너
 * "이거 필요없다"). 열 글자로 이름 붙은 열 중 값이 모두 정수인 열만 — 'AE열' 같은 글 칸은 그대로 보인다.
 * 시트에서는 그대로 있고 콘솔은 그 칸을 쓰지 않는다.
 */
function isRowNumberCol(key: string, rows: SheetMirrorRowDto[]): boolean {
  if (!/^[A-Z]{1,2}열$/.test(key)) return false;
  const vals = rows.map((r) => (r.cells[key] ?? '').trim()).filter(Boolean);
  return vals.length > 0 && vals.every((v) => /^\d+$/.test(v));
}

/** 칸 값별 건수(많은 것 먼저) — 빈 값은 뺀다. */
function countBy(rows: SheetMirrorRowDto[], key: string): [string, number][] {
  const m = new Map<string, number>();
  for (const r of rows) {
    const v = (r.cells[key] ?? '').trim();
    if (v) m.set(v, (m.get(v) ?? 0) + 1);
  }
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}

/** '2026-10' → '26.10' */
function monthLabel(month: string): string {
  return `${month.slice(2, 4)}.${month.slice(5, 7)}`;
}

const page: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  // 좌우는 레이아웃 여백(24)만 — 화면 여백이 겹쳐 48 이던 것을 반으로(2026-10-23 오너 "여기 여백을 반으로")
  // 여백은 레이아웃 여백(24)만 — 위쪽도 다른 화면과 같게(2026-10-23 오너 "윗쪽 간격도 다른 탭들에 비해 좀 큰거같은데")
  padding: 0,
  minWidth: 0,
};
/** [+ 새 줄] 과 표 — 표 바로 왼쪽 위에 붙여 둔다(2026-10-23 오너 "표 왼쪽 위에 보이게") */
const tableBlock: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 };
const notice: CSSProperties = {
  padding: '10px 14px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-warning-subtle)',
  color: 'var(--fg-on-warning-tint)',
  fontSize: 13,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};
const chip: CSSProperties = {
  height: 28,
  padding: '0 12px',
  borderRadius: 999,
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 12,
  cursor: 'pointer',
};
const chipOn: CSSProperties = {
  background: 'var(--brand)',
  border: '1px solid var(--brand)',
  color: 'var(--brand-on)',
};
const checkLabel: CSSProperties = { display: 'inline-flex', gap: 6, alignItems: 'center' };
const linkBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 32,
  padding: '0 14px',
  borderRadius: 'var(--r-btn)',
  border: '1px solid var(--border-default)',
  color: 'var(--fg-default)',
  textDecoration: 'none',
  fontSize: 13,
  fontWeight: 500,
};
